import styled, { css } from 'styled-components';
import { duration } from '@stylesheets/motion';
import { APPS_PHONE_MEDIA } from '../../pages/Apps/apps.constants';
import {
  HOVER_MEDIA,
  TILE_OPEN_PX,
  TILE_PHONE_PX,
  TILE_PX,
  TILE_RADIUS_PX,
} from './AppsFooter.constants';

const REVEAL_MS = 160;

const focusRing = (offset: string) => css`
  &:focus-visible {
    outline: 2px solid ${({ theme: { colors } }) => colors.primary};
    outline-offset: ${offset};
  }
`;

const tileBox = css`
  position: relative;
  width: ${TILE_PX}px;
  height: ${TILE_PX}px;
  border-radius: ${TILE_RADIUS_PX}px;

  ${APPS_PHONE_MEDIA} {
    width: ${TILE_PHONE_PX}px;
    height: ${TILE_PHONE_PX}px;
  }
`;

export const Reveal = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px;
  text-align: center;
  opacity: 0;
  visibility: hidden;
  /* The face underneath stays the click target; only the reveal's own buttons take clicks. */
  pointer-events: none;
  transition:
    opacity ${REVEAL_MS}ms ease,
    visibility 0s linear ${REVEAL_MS}ms;
  backdrop-filter: blur(6px);
  background: ${({ theme: { colors } }) =>
    `color-mix(in srgb, ${colors.surfaceContainer} 90%, transparent)`};

  & button {
    pointer-events: auto;
  }
`;

const revealed = css`
  ${Reveal} {
    opacity: 1;
    visibility: visible;
    transition: opacity ${REVEAL_MS}ms ease;
  }
`;

export const TileRoot = styled.li<{ $grow: boolean }>`
  ${tileBox}
  overflow: hidden;

  ${({ theme: { colors } }) => css`
    border: 1px solid ${colors.outlineVariant};
    background: ${colors.surfaceContainer};
    color: ${colors.onSurface};
    transition:
      width 200ms cubic-bezier(0.4, 0, 0.2, 1),
      border-color ${duration.fast} ease;
  `}

  &[data-open] {
    ${revealed}
  }

  ${HOVER_MEDIA} {
    &:hover,
    &:focus-within {
      border-color: ${({ theme: { colors } }) => colors.outline};
      ${({ $grow }) => $grow && `width: ${TILE_OPEN_PX}px;`}
      ${revealed}
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    ${Reveal} {
      transition: none;
    }
  }
`;

export const Face = styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 0 36px;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  ${focusRing('-2px')}
`;

export const Cover = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const Glyph = styled.span`
  display: inline-flex;
  color: ${({ theme: { colors } }) => colors.onSurface};
`;

export const Name = styled.span<{ $scrim?: boolean }>`
  position: absolute;
  inset: auto 0 0;
  padding: 24px 12px 16px;
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ $scrim, theme: { colors } }) =>
    $scrim && `background: linear-gradient(to top, ${colors.surfaceContainer} 45%, transparent);`}

  ${APPS_PHONE_MEDIA} {
    padding-bottom: 12px;
    font-size: 13px;
  }
`;

export const Check = styled.span`
  position: absolute;
  top: 12px;
  left: 14px;
  display: inline-flex;
  color: ${({ theme: { colors } }) => colors.success};
`;

export const RevealText = styled.p`
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  font-size: 14px;
  line-height: 1.45;
  color: ${({ theme: { colors } }) => colors.onSurfaceVariant};
`;

export const RevealRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
`;

const pill = css`
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 18px;
  border: none;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.full};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`;

export const OpenPill = styled.span`
  ${pill}
  background: ${({ theme: { colors } }) => colors.primary};
  color: ${({ theme: { colors } }) => colors.onPrimary};
`;

export const PillButton = styled.button<{ $primary?: boolean }>`
  ${pill}
  cursor: pointer;
  ${focusRing('2px')}

  ${({ $primary, theme: { colors } }) =>
    $primary
      ? css`
          background: ${colors.primaryContainer};
          color: ${colors.onPrimaryContainer};
        `
      : css`
          background: ${colors.surfaceContainerHighest};
          color: ${colors.onSurface};
          text-decoration: underline;
          text-underline-offset: 3px;
        `}

  &:hover {
    filter: brightness(1.08);
  }
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 12px;

  ${({ theme: { colors, borderRadius } }) => css`
    border: 1px solid ${colors.outlineVariant};
    border-radius: ${borderRadius.full};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const CreateRoot = styled.li`
  ${tileBox}
`;

export const CreateFace = styled(Face)`
  gap: 4px;
  padding: 12px;
  border-radius: inherit;

  ${({ theme: { colors } }) => css`
    border: 1px dashed ${colors.outline};
    transition: background-color ${duration.fast} ease;

    &:hover {
      background: ${colors.surfaceContainer};
    }
  `}
`;

export const CreateIcon = styled.span`
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  margin-bottom: 10px;
  border-radius: 50%;

  ${({ theme: { colors } }) => css`
    background: ${colors.surfaceContainerHighest};
    color: ${colors.primary};
  `}

  ${APPS_PHONE_MEDIA} {
    width: 40px;
    height: 40px;
    margin-bottom: 4px;
  }
`;

export const CreateTitle = styled.strong`
  font-size: 15px;
  font-weight: 600;
`;

export const CreateHint = styled.span`
  font-size: 13px;
  color: ${({ theme: { colors } }) => colors.onSurfaceVariant};
`;
